import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { decryptData, encryptData } from '../encryptionUtil';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
jsPDF.API.autoTable = autoTable;

const db = getFirestore();

export const generateInvoicePDF = async (invoiceData, docObject = null) => {
  console.log('InvoicePDFGenerator received:', invoiceData);
  const { practice, entries, total, invoiceNumber, profile, rateType, rateValue } = invoiceData;
  console.log('[PDF DEBUG] Top-level: rateType is', rateType, '| entries:', entries);
  
  if (!practice || !profile || !entries || entries.length === 0) {
    console.error('Missing required data for PDF generation', { 
      hasPractice: !!practice, 
      hasProfile: !!profile,
      entriesLength: entries?.length 
    });
    throw new Error('Missing required data for invoice generation');
  }
  
  try {
    // Use the provided doc object if it exists, otherwise create a new one
    const doc = docObject || new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const rightMargin = pageWidth - margin;
    const purple = [128, 0, 128];

    // Helper functions
    const safeText = (text, x, y, options = {}) => {
      // Ensure text is not null, undefined, 'NA', or an empty string before rendering
      if (text != null && text !== 'undefined' && text !== 'NA' && String(text).trim() !== '') {
        doc.text(String(text), x, y, options);
        return true; // Indicate that text was rendered
      }
      return false; // Indicate that text was not rendered
    };

    // Format date helper function to ensure clean date display
    const formatDate = (dateValue) => {
      if (!dateValue) return '';
      
      try {
        // If it's already a string in the right format, return it
        if (typeof dateValue === 'string' && !dateValue.includes('GMT')) {
          return dateValue;
        }
        
        // If it's a Date object or timestamp string, format it properly
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return dateValue; // Return original if invalid
        
        // Format as "Feb 24 2025" - month name, day, year
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return String(dateValue); // Fallback to string representation
      }
    };

    // Title - largest size
    doc.setFontSize(20);
    doc.setTextColor(...purple);
    safeText('INVOICE', pageWidth / 2, 20, { align: 'center' });

    // Reset text color for rest of document
    doc.setTextColor(0, 0, 0);

    // Addresses and main content - medium size
    doc.setFontSize(10); // Reduced from 11
    let yPos = 32; // Moved up from 40
    const lineSpacing = 5; // Reduced from 7 for more compact layout

    // From section (Left side) - Dynamic rendering (Uses profile.address1, etc.) - OK
    let currentY = yPos;
    if (profile) {
      if (safeText(profile.name, margin, currentY)) currentY += lineSpacing;
      // Add business name if it exists
      if (safeText(profile.businessName, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address1, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address2, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address3, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address4, margin, currentY)) currentY += lineSpacing;
      const cityPostcode = [profile.city, profile.postcode].filter(Boolean).join(', ');
      if (safeText(cityPostcode, margin, currentY)) currentY += lineSpacing;
      // Add email and phone if they exist
      if (safeText(profile.email, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.phone, margin, currentY)) currentY += lineSpacing;
    }
    const fromSectionEnd = currentY;

    // To section (Right side) - Dynamic rendering (Uses practice.address1, etc.) - OK
    currentY = yPos;
    if (practice) {
      if (safeText(practice.prac_name, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address1, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address2, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address3, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address4, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      // Combine address5 and postcode if they exist
      const address5Postcode = [practice.address5, practice.postcode].filter(Boolean).join(', ');
       if (safeText(address5Postcode, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
    }
    const toSectionEnd = currentY;

    // Adjust yPos for the next section based on the longest address block
    yPos = Math.max(fromSectionEnd, toSectionEnd) + 8; // Reduced padding from 15

    // Invoice details
    // Include invoice number in the document
    safeText(`Invoice Number: ${invoiceNumber || 'PREVIEW'}`, margin, yPos);
    safeText(`Date: ${format(new Date(), 'dd/MM/yyyy')}`, rightMargin, yPos, { align: 'right' });

    // Flatten entries for the table (handle all rate types)
    let tableData = [];
    if (rateType === 'percentage') {
      console.log('[PDF DEBUG] Entered percentage rateType block - FIRST FUNCTION');
      
      // Calculate totals
      let totalCollections = 0;
      const percentageRate = parseFloat(rateValue) || 0;
      
      // Group all patient types for the same date into a single line, showing collections (no percentage)
      const dateMap = new Map();
      entries.forEach(entry => {
        const dateKey = formatDate(entry.date || entry.formattedDate || '');
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { collections: 0, patientDetails: [] });
        }
        if (Array.isArray(entry.patients)) {
          entry.patients.forEach(patient => {
            const count = parseInt(patient.count) || 0;
            const rate = parseFloat(patient.rate) || 0;
            const description = patient.description || '';
            const patientCollections = count * rate;
            
            dateMap.get(dateKey).collections += patientCollections;
            dateMap.get(dateKey).patientDetails.push(
              `${count} patient${count !== 1 ? 's' : ''} @ £${rate.toFixed(2)}${description ? ` (${description})` : ''}`
            );
          });
        }
      });
      
      // Add daily rows showing patient collections (no percentage applied)
      dateMap.forEach((obj, dateKey) => {
        const patientDetailString = obj.patientDetails.join(' + ');
        totalCollections += obj.collections;
        
        tableData.push([
          dateKey,
          patientDetailString,
          `£${obj.collections.toFixed(2)}`
        ]);
      });
      
      // Calculate the percentage amount
      const percentageAmount = totalCollections * (percentageRate / 100);
      
      // Add summary rows
      tableData.push([
        { content: 'Total Charged to Patients:', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: `£${totalCollections.toFixed(2)}`, styles: { fontStyle: 'bold' } }
      ]);
      
      tableData.push([
        { content: `Commission (${percentageRate}%):`, colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: `£${percentageAmount.toFixed(2)}`, styles: { fontStyle: 'bold' } }
      ]);
      
      tableData.push([
        { content: 'Invoice Total:', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold', fillColor: [230, 230, 230] } },
        { content: `£${percentageAmount.toFixed(2)}`, styles: { fontStyle: 'bold', fillColor: [230, 230, 230] } }
      ]);
      
    } else if (rateType === 'per_patient') {
      console.log('[PDF DEBUG] Entered per_patient rateType block');
      // Group all patient types for the same date into a single line, similar to percentage but without percentage calculation
      const dateMap = new Map();
      entries.forEach(entry => {
        const dateKey = formatDate(entry.date || entry.formattedDate || '');
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { detailsArr: [], descriptions: [] });
        }
        if (Array.isArray(entry.patients)) {
          entry.patients.forEach(patient => {
            const count = parseInt(patient.count) || 0;
            const rate = parseFloat(patient.rate) || 0;
            const description = patient.description || '';
            dateMap.get(dateKey).detailsArr.push(`${count} × £${rate.toFixed(2)}${description ? ` (${description})` : ''}`);
            if (description) dateMap.get(dateKey).descriptions.push(description);
          });
        }
      });
      // Now, for each date, combine all patient types into one line
      dateMap.forEach((obj, dateKey) => {
        // Calculate total for this date
        let totalAmount = 0;
        // Always build the full calculation string for the description (robust for both flows)
        let calculationParts = [];
        // Find all entries for this date (should be one, but robust for both flows)
        const entriesForDate = entries.filter(entry => {
          const entryDate = formatDate(entry.date || entry.formattedDate || '');
          return entryDate === dateKey;
        });
        entriesForDate.forEach(entry => {
          if (Array.isArray(entry.patients)) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              const desc = patient.description ? ` (${patient.description})` : '';
              calculationParts.push(`${count} × £${rate.toFixed(2)}${desc}`);
              totalAmount += count * rate; // No percentage calculation for per_patient
            });
          }
        });
        const entryDescription = calculationParts.join(', ');
        // DEBUG: Log the descriptions being used for this date
        console.log(`[PDF DEBUG] Date: ${dateKey}, Description Used:`, entryDescription);
        tableData.push([
          dateKey,
          entryDescription,
          `£${totalAmount.toFixed(2)}`
        ]);
      });
    } else {
      entries.forEach(entry => {
        if (rateType === 'hourly') {
          // For hourly, each entry is a line item
          const hours = parseFloat(entry.hours) || 0;
          const hourlyRate = parseFloat(rateValue) || 0;
          const amount = hours * hourlyRate;
          let description = entry.description || '';
          if (description.includes('=')) {
            description = description.split('=')[0].trim();
          }
          tableData.push([
            formatDate(entry.date || entry.formattedDate || ''),
            `${hours} hours × £${hourlyRate.toFixed(2)}${description ? ` (${description})` : ''}`,
            `£${amount.toFixed(2)}`
          ]);
        } else if (rateType === 'fixed') {
          // For fixed, each entry is a line item
          const fixedAmount = parseFloat(rateValue) || 0;
          let description = entry.description || '';
          if (description.includes('=')) {
            description = description.split('=')[0].trim();
          }
          tableData.push([
            formatDate(entry.date || entry.formattedDate || ''),
            `Fixed rate${description ? ` (${description})` : ''}`,
            `£${fixedAmount.toFixed(2)}`
          ]);
        } else {
          // Fallback: show whatever is in entry.amount
          let description = entry.description || '';
          if (description.includes('=')) {
            description = description.split('=')[0].trim();
          }
          const amount = typeof entry.amount === 'number' ? 
            `£${entry.amount.toFixed(2)}` : 
            entry.amount?.toString() || '£0.00';
          tableData.push([
            formatDate(entry.date || entry.formattedDate || ''),
            description,
            amount
          ]);
        }
      });
    }

    // Table - Use autoTable function directly
    yPos += 8; // FIRST FUNC - Reduced // Adjust spacing after invoice details
    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Description', 'Amount']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 9,
        cellPadding: 2,
        font: 'helvetica'
      },
      headStyles: { 
        fillColor: [128, 0, 128],
        fontSize: 10,
        font: 'helvetica',
        fontStyle: 'bold'
      }
    });

    // Total - slightly larger for emphasis
    yPos = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    
    // Format total as currency (support object or number)
    let totalAmount = 0;
    if (typeof total === 'number') {
      totalAmount = total;
    } else if (total && typeof total.amount === 'number') {
      totalAmount = total.amount;
    } else if (typeof total === 'string') {
      totalAmount = parseFloat(total) || 0;
    }
    const formattedTotal = `£${totalAmount.toFixed(2)}`;
    safeText(`Total: ${formattedTotal}`, rightMargin, yPos, { align: 'right' });

    // Bank details and payment terms - smaller size (Uses profile.bankName, etc.) - OK
    if (profile) {
      yPos = doc.lastAutoTable.finalY + 20; // Position relative to table end
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Bank details section - Dynamic rendering
      let bankY = yPos;
      if (safeText('Bank Details:', margin, bankY)) bankY += lineSpacing;
      if (safeText(`Bank: ${profile.bankName}`, margin, bankY)) bankY += lineSpacing; // Uses profile.bankName - OK
      if (safeText(`Sort Code: ${profile.sortCode}`, margin, bankY)) bankY += lineSpacing; // Uses profile.sortCode - OK
      if (safeText(`Account Number: ${profile.accountNumber}`, margin, bankY)) bankY += lineSpacing; // Uses profile.accountNumber - OK

      // Payment terms
      yPos = bankY + lineSpacing; // Position after bank details
      safeText('Payment is due within 30 days from date of invoice', margin, yPos);
      // Add notes if they exist
      if (profile.notes) {
         yPos += lineSpacing;
         // Split notes by newline and render each line
         const noteLines = profile.notes.split('\n');
         noteLines.forEach(line => {
            if (safeText(line, margin, yPos)) {
               yPos += lineSpacing;
            }
         });
      }
    }

    // Generate filename with practice name and invoice number
    const practiceName = (practice.prac_name || practice.name)?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    // Log the practice object and practiceName for debugging filename issues
    console.log('PDF filename generation debug:', {
      practice,
      practiceName,
      invoiceNumber,
      dateStr
    });
    const filename = `${practiceName}_invoice_${invoiceNumber || dateStr}.pdf`;
    
    // IMPORTANT: Don't save the PDF here, return the doc object for the caller to save
    // This prevents duplicate PDF generation
    console.log('PDF generated, returning without saving');
    
    // Return the doc object and filename for the caller to use
    return { doc, filename };
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

// NEW FUNCTION: Generate PDF as Blob for opening in new tab
export const generateInvoicePDFBlob = async (invoiceData) => {
  console.log('InvoicePDFGenerator (Blob) received:', invoiceData);
  const { practice, entries, total, invoiceNumber, profile, rateType, rateValue } = invoiceData;

  if (!practice || !profile || !entries || entries.length === 0) {
    console.error('Missing required data for PDF generation', { 
      hasPractice: !!practice, 
      hasProfile: !!profile,
      entriesLength: entries?.length 
    });
    throw new Error('Missing required data for invoice generation');
  }

  try {
    // Create a new jsPDF instance
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const rightMargin = pageWidth - margin;
    const purple = [128, 0, 128];

    // Helper functions
    const safeText = (text, x, y, options = {}) => {
      // Ensure text is not null, undefined, 'NA', or an empty string before rendering
      if (text != null && text !== 'undefined' && text !== 'NA' && String(text).trim() !== '') {
        doc.text(String(text), x, y, options);
        return true; // Indicate that text was rendered
      }
      return false; // Indicate that text was not rendered
    };

    // Format date helper function to ensure clean date display
    const formatDate = (dateValue) => {
      if (!dateValue) return '';
      
      try {
        // If it's already a string in the right format, return it
        if (typeof dateValue === 'string' && !dateValue.includes('GMT')) {
          return dateValue;
        }
        
        // If it's a Date object or timestamp string, format it properly
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return dateValue; // Return original if invalid
        
        // Format as "Feb 24 2025" - month name, day, year
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return String(dateValue); // Fallback to string representation
      }
    };

    // Title - largest size
    doc.setFontSize(20);
    doc.setTextColor(...purple);
    safeText('INVOICE', pageWidth / 2, 20, { align: 'center' });

    // Reset text color for rest of document
    doc.setTextColor(0, 0, 0);

    // Addresses and main content - medium size
    doc.setFontSize(10); // Reduced from 11
    let yPos = 32; // Moved up from 40
    const lineSpacing = 5; // Reduced from 7 for compact layout

    // From section (Left side) - Dynamic rendering (Uses profile.address1, etc.) - OK
    let currentY = yPos;
    if (profile) {
      if (safeText(profile.name, margin, currentY)) currentY += lineSpacing;
      // Add business name if it exists
      if (safeText(profile.businessName, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address1, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address2, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address3, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.address4, margin, currentY)) currentY += lineSpacing;
      const cityPostcode = [profile.city, profile.postcode].filter(Boolean).join(', ');
      if (safeText(cityPostcode, margin, currentY)) currentY += lineSpacing;
      // Add email and phone if they exist
      if (safeText(profile.email, margin, currentY)) currentY += lineSpacing;
      if (safeText(profile.phone, margin, currentY)) currentY += lineSpacing;
    }
    const fromSectionEnd = currentY;

    // To section (Right side) - Dynamic rendering (Uses practice.address1, etc.) - OK
    currentY = yPos;
    if (practice) {
      if (safeText(practice.prac_name, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address1, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address2, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address3, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      if (safeText(practice.address4, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
      // Combine address5 and postcode if they exist
      const address5Postcode = [practice.address5, practice.postcode].filter(Boolean).join(', ');
       if (safeText(address5Postcode, rightMargin, currentY, { align: 'right' })) currentY += lineSpacing;
    }
    const toSectionEnd = currentY;

    // Adjust yPos for the next section based on the longest address block
    yPos = Math.max(fromSectionEnd, toSectionEnd) + 8; // Reduced padding from 15

    // Invoice details
    // Include invoice number in the document
    safeText(`Invoice Number: ${invoiceNumber || 'PREVIEW'}`, margin, yPos);
    safeText(`Date: ${format(new Date(), 'dd/MM/yyyy')}`, rightMargin, yPos, { align: 'right' });

    // Flatten entries for the table (handle all rate types)
    let tableData = [];
    if (rateType === 'percentage') {
      console.log('[PDF DEBUG] Entered percentage rateType block - SECOND FUNCTION');
      // Calculate totals
      let totalCollections = 0;
      const percentageRate = parseFloat(rateValue) || 0;
      
      // Group all patient types for the same date into a single line, showing collections (no percentage)
      const dateMap = new Map();
      entries.forEach(entry => {
        const dateKey = formatDate(entry.date || entry.formattedDate || '');
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { collections: 0, patientDetails: [] });
        }
        if (Array.isArray(entry.patients)) {
          entry.patients.forEach(patient => {
            const count = parseInt(patient.count) || 0;
            const rate = parseFloat(patient.rate) || 0;
            const description = patient.description || '';
            const patientCollections = count * rate;
            
            dateMap.get(dateKey).collections += patientCollections;
            dateMap.get(dateKey).patientDetails.push(
              `${count} patient${count !== 1 ? 's' : ''} @ £${rate.toFixed(2)}${description ? ` (${description})` : ''}`
            );
          });
        }
      });
      
      // Add daily rows showing patient collections (no percentage applied)
      dateMap.forEach((obj, dateKey) => {
        const patientDetailString = obj.patientDetails.join(' + ');
        totalCollections += obj.collections;
        
        tableData.push([
          dateKey,
          patientDetailString,
          `£${obj.collections.toFixed(2)}`
        ]);
      });
      
      // Calculate the percentage amount
      const percentageAmount = totalCollections * (percentageRate / 100);
      
      // Add summary rows
      tableData.push([
        { content: 'Total Charged to Patients:', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: `£${totalCollections.toFixed(2)}`, styles: { fontStyle: 'bold' } }
      ]);
      
      tableData.push([
        { content: `Commission (${percentageRate}%):`, colSpan: 2, styles: { halign: 'right', fontStyle: 'bold' } },
        { content: `£${percentageAmount.toFixed(2)}`, styles: { fontStyle: 'bold' } }
      ]);
      
      tableData.push([
        { content: 'Invoice Total:', colSpan: 2, styles: { halign: 'right', fontStyle: 'bold', fillColor: [230, 230, 230] } },
        { content: `£${percentageAmount.toFixed(2)}`, styles: { fontStyle: 'bold', fillColor: [230, 230, 230] } }
      ]);
      
    } else if (rateType === 'per_patient') {
      // Group all patient types for the same date into a single line, similar to percentage but without percentage calculation
      const dateMap = new Map();
      entries.forEach(entry => {
        const dateKey = formatDate(entry.date || entry.formattedDate || '');
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, []);
        }
        if (Array.isArray(entry.patients)) {
          entry.patients.forEach(patient => {
            const count = parseInt(patient.count) || 0;
            const rate = parseFloat(patient.rate) || 0;
            const description = patient.description || '';
            dateMap.get(dateKey).push(`${count} × £${rate.toFixed(2)}${description ? ` (${description})` : ''}`);
          });
        }
      });
      // Now, for each date, combine all patient types into one line
      dateMap.forEach((detailsArr, dateKey) => {
        // Calculate total for this date
        let totalAmount = 0;
        entries.forEach(entry => {
          const entryDate = formatDate(entry.date || entry.formattedDate || '');
          if (entryDate === dateKey && Array.isArray(entry.patients)) {
            entry.patients.forEach(patient => {
              const count = parseInt(patient.count) || 0;
              const rate = parseFloat(patient.rate) || 0;
              totalAmount += count * rate; // No percentage calculation for per_patient
            });
          }
        });
        tableData.push([
          dateKey,
          detailsArr.join(', '),
          `£${totalAmount.toFixed(2)}`
        ]);
      });
    } else {
      entries.forEach(entry => {
        if (rateType === 'hourly') {
          // For hourly, each entry is a line item
          const hours = parseFloat(entry.hours) || 0;
          const hourlyRate = parseFloat(rateValue) || 0;
          const amount = hours * hourlyRate;
          let description = entry.description || '';
          if (description.includes('=')) {
            description = description.split('=')[0].trim();
          }
          tableData.push([
            formatDate(entry.date || entry.formattedDate || ''),
            `${hours} hours × £${hourlyRate.toFixed(2)}${description ? ` (${description})` : ''}`,
            `£${amount.toFixed(2)}`
          ]);
        } else if (rateType === 'fixed') {
          // For fixed, each entry is a line item
          const fixedAmount = parseFloat(rateValue) || 0;
          let description = entry.description || '';
          if (description.includes('=')) {
            description = description.split('=')[0].trim();
          }
          tableData.push([
            formatDate(entry.date || entry.formattedDate || ''),
            `Fixed rate${description ? ` (${description})` : ''}`,
            `£${fixedAmount.toFixed(2)}`
          ]);
        } else {
          // Fallback: show whatever is in entry.amount
          let description = entry.description || '';
          if (description.includes('=')) {
            description = description.split('=')[0].trim();
          }
          const amount = typeof entry.amount === 'number' ? 
            `£${entry.amount.toFixed(2)}` : 
            entry.amount?.toString() || '£0.00';
          tableData.push([
            formatDate(entry.date || entry.formattedDate || ''),
            description,
            amount
          ]);
        }
      });
    }

    // SECOND_FUNC: Table - Use autoTable function directly
    yPos += 8; // Reduced spacing after invoice details from 15
    autoTable(doc, {
      startY: yPos,
      head: [['Date', 'Description', 'Amount']],
      body: tableData,
      theme: 'grid',
      styles: { 
        fontSize: 9, // Reduced from 10
        cellPadding: 2, // Reduced from 5
        font: 'helvetica'
      },
      headStyles: { 
        fillColor: [128, 0, 128],
        fontSize: 9, // Reduced from 10
        font: 'helvetica',
        fontStyle: 'bold'
      }
    });

    // Total - slightly larger for emphasis
    yPos = doc.lastAutoTable.finalY + 8; // Reduced from 10
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    
    // Format total as currency (support object or number) - SECOND FUNC
    let totalAmount = 0;
    if (typeof total === 'number') {
      totalAmount = total;
    } else if (total && typeof total.amount === 'number') {
      totalAmount = total.amount;
    } else if (typeof total === 'string') {
      totalAmount = parseFloat(total) || 0;
    }
    const formattedTotal = `£${totalAmount.toFixed(2)}`;
    safeText(`Total: ${formattedTotal}`, rightMargin, yPos, { align: 'right' });

    // Bank details and payment terms - smaller size (Uses profile.bankName, etc.) - OK
    if (profile) {
      yPos = doc.lastAutoTable.finalY + 20; // Position relative to table end
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      // Bank details section - Dynamic rendering
      let bankY = yPos;
      if (safeText('Bank Details:', margin, bankY)) bankY += lineSpacing;
      if (safeText(`Bank: ${profile.bankName}`, margin, bankY)) bankY += lineSpacing; // Uses profile.bankName - OK
      if (safeText(`Sort Code: ${profile.sortCode}`, margin, bankY)) bankY += lineSpacing; // Uses profile.sortCode - OK
      if (safeText(`Account Number: ${profile.accountNumber}`, margin, bankY)) bankY += lineSpacing; // Uses profile.accountNumber - OK

      // Payment terms
      yPos = bankY + lineSpacing; // Position after bank details
      safeText('Payment is due within 30 days from date of invoice', margin, yPos);
      // Add notes if they exist
      if (profile.notes) {
         yPos += lineSpacing;
         // Split notes by newline and render each line
         const noteLines = profile.notes.split('\n');
         noteLines.forEach(line => {
            if (safeText(line, margin, yPos)) {
               yPos += lineSpacing;
            }
         });
      }
    }

    // Generate filename
    const practiceName = (practice.prac_name || practice.name)?.replace(/\s+/g, '_').toLowerCase() || 'invoice';
    const dateStr = new Date().toISOString().split('T')[0];
    // Log the practice object and practiceName for debugging filename issues (Blob)
    console.log('PDF Blob filename generation debug:', {
      practice,
      practiceName,
      invoiceNumber,
      dateStr
    });
    const filename = `${practiceName}_invoice_${invoiceNumber || dateStr}.pdf`;

    // Return ONLY the Blob, do not call doc.save()
    console.log('PDF Blob generated successfully');
    const pdfBlob = doc.output('blob');

    return pdfBlob;
  } catch (error) {
    console.error('Error generating PDF Blob:', error);
    throw error;
  }
};

